// src/pages/CompPlanSeeder.js
// One-shot tool to seed the compPlans collection from the COMP_PLAN_SEEDS array.
// Admin only. Safe to re-run — uses setDoc with merge so it updates in place.
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { COMP_PLAN_SEEDS, SPECTRUM_PRODUCTS_FULL } from '../data/compPlanSeeds';

export default function CompPlanSeeder() {
  const [existing, setExisting] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    async function load() {
      const snap = await getDocs(collection(db, 'compPlans'));
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      setExisting(map);
      setLoading(false);
    }
    load();
  }, []);

  async function runSeed() {
    setRunning(true);
    setLog([]);
    const out = [];
    for (const plan of COMP_PLAN_SEEDS) {
      try {
        await setDoc(doc(db, 'compPlans', plan.id), plan, { merge: true });
        out.push({ id: plan.id, name: plan.name, status: existing[plan.id] ? 'updated' : 'created' });
      } catch (err) {
        out.push({ id: plan.id, name: plan.name, status: 'error', error: err.message });
      }
      setLog([...out]);
    }
    setRunning(false);
  }

  if (loading) return <div style={S.page}><div style={S.loading}>Loading…</div></div>;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>Comp plan seeder</h1>
        <p style={S.sub}>One-shot import of all comp plans transcribed from the source system. Safe to re-run.</p>
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Plans to write</div>
        <div style={S.stats}>
          <div style={S.stat}><div style={S.statLbl}>Total in seed</div><div style={S.statVal}>{COMP_PLAN_SEEDS.length}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Already in DB</div><div style={{ ...S.statVal, color: '#f5a623' }}>{COMP_PLAN_SEEDS.filter(p => existing[p.id]).length}</div></div>
          <div style={S.stat}><div style={S.statLbl}>New</div><div style={{ ...S.statVal, color: '#00d4a0' }}>{COMP_PLAN_SEEDS.filter(p => !existing[p.id]).length}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Products / plan</div><div style={S.statVal}>{SPECTRUM_PRODUCTS_FULL.length}</div></div>
        </div>

        <table style={S.table}>
          <thead><tr>{['ID', 'Name', 'Type', 'In DB?'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {COMP_PLAN_SEEDS.map(p => (
              <tr key={p.id}>
                <td style={{ ...S.td, color: '#888', fontFamily: 'monospace', fontSize: 11 }}>{p.id}</td>
                <td style={S.td}>{p.name}</td>
                <td style={{ ...S.td, color: p.planType === 'ISO' ? '#f5a623' : '#888' }}>{p.planType}</td>
                <td style={S.td}>{existing[p.id] ? <span style={{ color: '#f5a623' }}>yes (will update)</span> : <span style={{ color: '#00d4a0' }}>no (will create)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={S.actions}>
          <button style={S.btn} onClick={runSeed} disabled={running}>
            {running ? 'Writing…' : `Write ${COMP_PLAN_SEEDS.length} plans to Firestore`}
          </button>
        </div>
      </div>

      {log.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>Progress · {log.length} / {COMP_PLAN_SEEDS.length}</div>
          <div style={S.logList}>
            {log.map(l => (
              <div key={l.id} style={S.logRow}>
                <span style={{ color: l.status === 'error' ? '#ff6b6b' : l.status === 'created' ? '#00d4a0' : '#f5a623', width: 80, fontSize: 11, textTransform: 'uppercase' }}>{l.status}</span>
                <span style={{ color: '#ccc', flex: 1 }}>{l.name}</span>
                {l.error && <span style={{ color: '#ff6b6b', fontSize: 11 }}>{l.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  loading: { color: '#555', padding: 40 },
  card: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 12, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 },
  stat: { background: '#0f0f18', borderRadius: 8, padding: '10px 12px' },
  statLbl: { display: 'block', fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  statVal: { fontSize: 22, fontWeight: 600, color: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 },
  th: { textAlign: 'left', padding: '6px 10px', color: '#555', fontWeight: 500, borderBottom: '1px solid #1e1e2a', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '8px 10px', color: '#ccc', borderBottom: '1px solid #0f0f18' },
  actions: { display: 'flex', gap: 12, alignItems: 'center' },
  btn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '12px 32px', color: '#000', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  logList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto' },
  logRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: '#0f0f18', borderRadius: 6 },
};
